/**
 * Lightweight filter data for query generation.
 * Constructs minimal filter objects without importing filter definition files,
 * avoiding transitive deps on smart-user-picker, @atlaskit/select, react-dom etc.
 *
 *
 */
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type {
	AllFilterStates,
	MinimalFilterValue,
} from '../../../../common/constants/filters/types';
import {
	all1pProductKeys,
	all3pProductKeys,
	ProductKeys,
	type ProductKeys1P,
} from '../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../common/utils/filters/get-products-3p';
import { createBooleanFilter } from '../../../filters/boolean-filter/create-boolean-filter';
import { createMultiSelectFilter } from '../../../filters/multi-select-filter';
import { createSingleSelectFilter } from '../../../filters/single-select-filter';

import { generateQueryParamsGetState as lastUpdatedGenerateQueryParamsGetState } from './last-updated/utils';
import { generateQueryParamsGetState as pageStatusGenerateQueryParamsGetState } from './page-status/generate-query-params-get-state';
import { generateQueryParamsGetState as positionTeamGenerateQueryParamsGetState } from './position-team/utils';
import { generateQueryParamsGetState as projectGenerateQueryParamsGetState } from './project/utils';

export type FilterDataEntry = {
	id: string;
	type: string;
	queryParams: {
		key: string;
		getState: (queryParams: Record<string, string[]>, cloudId: string) => AllFilterStates;
	};
	products: string[];
	universal: boolean;
};

// Helper product arrays
const confluenceProducts: ProductKeys1P[] = [ProductKeys.Confluence];
const jiraProducts: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];
const assetsProducts: ProductKeys1P[] = [ProductKeys.Assets];
const mercuryProducts: ProductKeys1P[] = [ProductKeys.Focus];
const talentProducts: ProductKeys1P[] = [ProductKeys.Talent];
const contributorProducts: ProductKeys1P[] = all1pProductKeys.filter(
	(key) => key !== ProductKeys.Trello && key !== ProductKeys.Focus && key !== ProductKeys.Talent,
);
const contributor3P = all3pProductKeys.filter((key) => key !== ProductKeys3P.Figma);

export const allFilterData: Record<string, MinimalFilterValue> = {
	lastUpdated: createMultiSelectFilter({
		id: 'lastUpdated',
		products: [
			ProductKeys.Atlas,
			ProductKeys.Confluence,
			ProductKeys.Focus,
			ProductKeys.Jira,
			ProductKeys.JiraProductDiscovery,
			ProductKeys.JiraServiceManagement,
			ProductKeys.Compass,
			ProductKeys.Trello,
			ProductKeys.Bitbucket,
			...getProducts3PFromConfigForFilter('lastUpdated'),
		],
		alwaysVisible: true,
		universal: true,
		queryParams: {
			key: 'lastModified',
			generateQueryParamsGetState: lastUpdatedGenerateQueryParamsGetState,
		},
	}),
	contributor: createMultiSelectFilter({
		id: 'contributor',
		products: [...contributorProducts, ...contributor3P],
		alwaysVisible: true,
		universal: true,
		queryParams: { key: 'contributors' },
	}),
	space: createMultiSelectFilter({
		id: 'space',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('space')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'spaces' },
	}),
	site: createMultiSelectFilter({
		id: 'site',
		products: [...all1pProductKeys, ...all3pProductKeys],
		alwaysVisible: false,
		universal: true,
		queryParams: { key: 'sites' },
	}),
	watchedByMe: createBooleanFilter({
		id: 'watchedByMe',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraProductDiscovery,
			ProductKeys.JiraServiceManagement,
		],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'jWatchedByMe' },
	}),
	verified: createBooleanFilter({
		id: 'verified',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('verified')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'verified' },
	}),
	assignee: createMultiSelectFilter({
		id: 'assignee',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('assignee')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jAssignee' },
	}),
	reporter: createMultiSelectFilter({
		id: 'reporter',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('reporter')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jReporter' },
	}),
	commentedByMe: createBooleanFilter({
		id: 'commentedByMe',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraProductDiscovery,
			ProductKeys.JiraServiceManagement,
			...getProducts3PFromConfigForFilter('commentedByMe'),
		],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'jCommentedByMe' },
	}),
	project: createMultiSelectFilter({
		id: 'project',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('project')],
		alwaysVisible: true,
		universal: false,
		queryParams: {
			key: 'jProject',
			generateQueryParamsGetState: projectGenerateQueryParamsGetState,
		},
	}),
	universalTypeFilter: createSingleSelectFilter({
		id: 'universalTypeFilter',
		products: [...all1pProductKeys, ...all3pProductKeys],
		alwaysVisible: true,
		universal: true,
		queryParams: { key: 'type' },
	}),
	content: createMultiSelectFilter({
		id: 'content',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('content')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'ancestor' },
	}),
	onlyTitleMatch: createBooleanFilter({
		id: 'onlyTitleMatch',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('onlyTitleMatch')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'titleMatch' },
	}),
	owner: createMultiSelectFilter({
		id: 'owner',
		products: [
			ProductKeys.Confluence,
			ProductKeys.Focus,
			...getProducts3PFromConfigForFilter('owner'),
		],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'owners' },
	}),
	createdBy: createMultiSelectFilter({
		id: 'createdBy',
		products: [...getProducts3PFromConfigForFilter('createdBy')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'createdBy' },
	}),
	label: createMultiSelectFilter({
		id: 'label',
		products: [
			ProductKeys.Confluence,
			ProductKeys.Jira,
			ProductKeys.JiraProductDiscovery,
			ProductKeys.JiraServiceManagement,
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'labels' },
	}),
	tag: createMultiSelectFilter({
		id: 'tag',
		products: [ProductKeys.Loom],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'tags' },
	}),
	focusAreaType: createMultiSelectFilter({
		id: 'focusAreaType',
		products: [...mercuryProducts],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'focusAreaTypes' },
	}),
	focusAreaUnder: createMultiSelectFilter({
		id: 'focusAreaUnder',
		products: [...mercuryProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'focusAreaUnder' },
	}),
	pageStatus: createMultiSelectFilter({
		id: 'pageStatus',
		products: [...confluenceProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: {
			key: 'pageStatus',
			generateQueryParamsGetState: pageStatusGenerateQueryParamsGetState,
		},
	}),
	archived: createBooleanFilter({
		id: 'archived',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('archived')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'archived' },
	}),
	lastUpdatedBy: createMultiSelectFilter({
		id: 'lastUpdatedBy',
		products: [ProductKeys.Salesforce, ProductKeys.Hubspot],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'lastUpdatedBy' },
	}),
	statusFilter: createMultiSelectFilter({
		id: 'statusFilter',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraProductDiscovery,
			ProductKeys.JiraServiceManagement,
			...getProducts3PFromConfigForFilter('statusFilter'),
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'status' },
	}),
	compassArchived: createBooleanFilter({
		id: 'compassArchived',
		products: [ProductKeys.Compass],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'compassArchived' },
	}),
	assetsSchema: createMultiSelectFilter({
		id: 'assetsSchema',
		products: [...assetsProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'assetsSchema' },
	}),
	assetsType: createMultiSelectFilter({
		id: 'assetsType',
		products: [...assetsProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'assetsType' },
	}),
	positionStatus: createMultiSelectFilter({
		id: 'positionStatus',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionStatuses' },
	}),
	positionEmploymentType: createMultiSelectFilter({
		id: 'positionEmploymentType',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionEmploymentTypes' },
	}),
	positionJobFamily: createMultiSelectFilter({
		id: 'positionJobFamily',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionJobFamilies' },
	}),
	positionTeam: createMultiSelectFilter({
		id: 'positionTeam',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: {
			key: 'positionTeams',
			generateQueryParamsGetState: positionTeamGenerateQueryParamsGetState,
		},
	}),
	positionFocusArea: createMultiSelectFilter({
		id: 'positionFocusArea',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionFocusAreas' },
	}),
	opportunityStatus: createMultiSelectFilter({
		id: 'opportunityStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'opportunityStatus' },
	}),
	campaignStatus: createMultiSelectFilter({
		id: 'campaignStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'campaignStatuses' },
	}),
	caseStatus: createMultiSelectFilter({
		id: 'caseStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'caseStatuses' },
	}),
	documentStatus: createMultiSelectFilter({
		id: 'documentStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'knowledgeArticleStatuses' },
	}),
	calendarType: createMultiSelectFilter({
		id: 'calendarType',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'eventTypes' },
	}),
	leadStatus: createMultiSelectFilter({
		id: 'leadStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'leadStatuses' },
	}),
	taskStatus: createMultiSelectFilter({
		id: 'taskStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'taskStatuses' },
	}),
	taskType: createMultiSelectFilter({
		id: 'taskType',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'taskTypes' },
	}),
	sort: createSingleSelectFilter({
		id: 'sort',
		products: [ProductKeys.Confluence, ProductKeys.Jira],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'sort' },
	}),
	salesforceStage: createMultiSelectFilter({
		id: 'salesforceStage',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'salesforceStages' },
	}),
	closeDate: createSingleSelectFilter({
		id: 'closeDate',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'closeDate' },
	}),
	pullRequestStatus: createMultiSelectFilter({
		id: 'pullRequestStatus',
		products: [ProductKeys.Bitbucket, ...getProducts3PFromConfigForFilter('pullRequestStatus')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'pullRequestStatus' },
	}),
	repository: createMultiSelectFilter({
		id: 'repository',
		products: [...getProducts3PFromConfigForFilter('repository')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'repository' },
	}),
	reviewer: createMultiSelectFilter({
		id: 'reviewer',
		products: [...getProducts3PFromConfigForFilter('reviewer')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'reviewer' },
	}),
	slackChannel: createMultiSelectFilter({
		id: 'slackChannel',
		products: getProducts3PFromConfigForFilter('slackChannel'),
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'slackChannels' },
	}),
	folder: createMultiSelectFilter({
		id: 'folder',
		// TODO: Add Loom product when end-to-end is ready
		products: [],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'folders' },
	}),
	workspaceName: createMultiSelectFilter({
		id: 'workspaceName',
		products: getProducts3PFromConfigForFilter('workspaceName'),
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'workspaceName' },
	}),
	hasAttachment: createBooleanFilter({
		id: 'hasAttachment',
		products: getProducts3PFromConfigForFilter('hasAttachment'),
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'hasAttachment' },
	}),
};
